import { Event } from "@planzone/events";
import { State, Selector } from "./types";
import { initialState } from "./initialState";
import { createSelector } from "./selector";
import { reduceZonesRetrievalNotificationState } from "./retrieveZones";
import { reduceZoneRetrievalNotificationState } from "./retrieveZone";
import { reduceZoneRemovalNotificationState } from "./removeZone";
import { reduceProjectsTransferNotificationState } from "./transferProjects";
import { reduceAddPlanzoneResourcesToProjectNotificationState } from "./addPlanzoneResourcesToProject";
import { reduceZoneCreationNotificationState } from "./createZone";
import { reduceZoneEditionNotificationState } from "./editZone";
import { reduceProjectCreationNotificationState } from "./createProject";
import { reduceAuthenticationNotificationState } from "./authenticate";
import { reduceTaskEditionNotificationState } from "./editTask";
import { reduceAccountCreationNotificationState } from "./createAccount";
import { reduceStartDiscussionNotificationState } from "./startDiscussion";
import { reduceAccountCreationConfirmationNotificationState } from "./confirmAccountCreation";
import { reduceTaskStatusCreationNotificationState } from "./createTaskStatus";
import { reduceTaskStatusEditionNotificationState } from "./editTaskStatus";
import { reduceUserPreferencesEditionNotificationState } from "./editUserPreferences";
import { reduceListCreationNotificationState } from "./createList";
import { reduceResourceGroupRemoveNotificationState } from "./removeResourceGroup";
import { reduceResourceGroupEditionNotificationState } from "./editResourceGroup";
import { reduceAddResourceToResourceGroupNotificationState } from "./addResourceToResourceGroup";
import { reduceAddResourceGroupNotificationState } from "./addResourceGroup";
import { reduceRemoveTaskState } from "./removeTask";
import { reduceAddPlanzoneMaterialResourceNotificationState } from "./addPlanzoneMaterialResource";
import { reduceAddPlanzoneVirtualResourceNotificationState } from "./addPlanzoneVirtualResource";
import { reduceTaskCreationNotificationState } from "./createTask";
import { reduceListEditionNotificationState } from "./editList";
import { reduceRemoveProjectState } from "./removeProject";
import { reduceRemoveListState } from "./removeList";
import { reduceAddProjectMaterialResourceNotificationState } from "./addProjectMaterialResource";
import { reduceAddProjectVirtualResourceNotificationState } from "./addProjectVirtualResource";
import { reduceAllocateResourcesNotificationState } from "./allocateResources";
import { reduceProjectWeatherReportEditionNotificationState } from "./editProjectWeatherReport";
import { reduceRemoveResourceFromProjectNotificationState } from "./removeResourceFromProject";
import { reduceEditAllocationNotificationState } from "./editAllocation";
import { reduceRemoveAllocationNotificationState } from "./removeAllocation";
import { reduceMilestoneEditionNotificationState } from "./editMilestone";
import { reduceRemoveTaskStatusState } from "./removeTaskStatus";
import { reduceAddPlanzoneResourceToProjectNotificationState } from "./addPlanzoneResourceToProject";
import { reduceAddProjectExternalLinkNotificationState } from "./addProjectExternalLink";
import { reduceAssignTagNotificationState } from "./assignTag";
import { reduceChangePlanzoneNotificationState } from "./changePlanzone";
import { reduceCreateMilestoneNotificationState } from "./createMilestone";
import { reduceEditPersonalWeeklyCalendarNotificationState } from "./editPersonalWeeklyCalendar";
import { reduceEditProjectNotificationState } from "./editProject";
import { reduceEditProjectExternalLinkNotificationState } from "./editProjectExternalLink";
import { reduceLogOutNotificationState } from "./logOut";
import { reduceMarkMilestoneAsCompletedNotificationState } from "./markMilestoneAsCompleted";
import { reduceMoveKanbanTaskNotificationState } from "./moveKanbanTask";
import { reduceMoveListElementNotificationState } from "./moveListElement";
import { reduceRemoveProjectExternalLinkNotificationState } from "./removeProjectExternalLink";
import { reduceRemoveProjectStatusNotificationState } from "./removeProjectStatus";
import { reduceCreateProjectStatusNotificationState } from "./createProjectStatus";
import { reduceRemoveTagNotificationState } from "./removeTag";
import { reduceEditProjectReportTemplateNotificationState } from "./editProjectReportTemplate";
import { reduceSubmitProjectReportNotificationState } from "./submitProjectReport";
import { reduceExportDataNotificationState } from "./exportData";
import { reduceExportBiRunNotificationState } from "./exportBiRun";
import { reduceConfigureProjectToolsNotificationState } from "./configureProjectTools";
import { reduceMarkTaskAsCompletedNotificationState } from "./markTaskAsCompleted";
import { reduceEditResourceNotificationState } from "./editResource";
import { reduceRemoveResourceFromPlanzoneNotificationState } from "./removeResourceFromPlanzone";
import { reduceUpdatePlanzoneContactsNotificationState } from "./updatePlanzoneContacts";
import { reduceRemoveResourceFromResourceGroupNotificationState } from "./removeResourceFromResourceGroup";
import { reduceEditProjectResourceNotificationState } from "./editProjectResource";
import { reduceEventCreationNotificationState } from "./createEvent";
import { reduceChangePasswordNotificationState } from "./changePassword";
import { reduceRemoveMilestoneState } from "./removeMilestone";
import { reduceCreateProjectModelFromProjectNotificationState } from "./createProjectModelFromProject";
import { reduceInviteMembersNotificationState } from "./inviteMembers";
import { reduceFillAccountNotificationState } from "./fillAccount";
import { reduceCreateProjectPortfolioNotificationState } from "./createProjectPortfolio";
import { reduceEditProjectPortfolioNotificationState } from "./editProjectPortfolio";
import { reduceSendActivityReportNotificationState } from "./sendActivityReport";
import { reduceSaveTimeRecordNotificationState } from "./saveTimeRecord";
import { reduceValidateTimeRecordsUpToDateNotificationState } from "./validateTimeRecordsUpToDate";
import { reduceEventEditionNotificationState } from "./editEvent";
import { reduceEventRetrievalNotificationState } from "./retrieveEvent";
import { reduceRemoveEventNotificationState } from "./removeEvent";
import { reduceMilestoneRetrievalNotificationState } from "./retrieveMilestoneError";
import { reduceTaskRetrievalNotificationState } from "./retrieveTask";
import { reduceListRetrievalNotificationState } from "./retrieveList";
import { reduceUnavailabilityCreationNotificationState } from "./createUnavailability";
import { reduceEditUnavailabilityNotificationState } from "./editUnavailability";
import { reduceUploadDocumentNotificationState } from "./uploadDocument";
import { reduceDownloadDocumentContentNotificationState } from "./downloadDocumentContent";
import { reduceDownloadFolderContentNotificationState } from "./downloadFolderContent";
import { reduceCreateCalendarHolidaysNotificationState } from "./createCalendarHolidays";
import { reduceRemoveCalendarHolidaysNotificationState } from "./deleteCalendarHolidays";
import { reduceRemoveHolidayNotificationState } from "./removeHoliday";
import { reduceUseProjectModelNotificationState } from "./useProjectModel";
import { reduceDuplicateProjectNotificationState } from "./duplicateProject";
import { reduceAdjustWorkloadNotificationState } from "./adjustWorkload";
import { reduceAdjustCapacityNotificationState } from "./adjustCapacity";
import { reduceRemoveOrganizationalUnitState } from "./removeOrganizationalUnit";
import { reduceMoveOrganizationalUnitState } from "./moveOrganizationalUnit";
import { reduceUpdateOrganizationalUnitState } from "./updateOrganizationalUnit";

function reduceNotificationDismissal(state: State, event: Event): State {
  return {
    ...state,
    queue: state.queue.filter(({ key }) => key !== event.data),
  };
}

function removeSuccessNotification(state: State): State {
  return {
    ...state,
    queue: state.queue.filter(
      (notification) =>
        !notification.flags.find(
          (flag) =>
            flag === "success" &&
            notification.key !== "changePassword" &&
            notification.key !== "sendActivityReport" &&
            notification.key !== "exportBiRun",
        ),
    ),
  };
}

function reduceState(state: State = initialState, event: Event): State {
  return [
    reduceNotificationDismissal,
    reduceZonesRetrievalNotificationState,
    reduceZoneRetrievalNotificationState,
    reduceZoneRemovalNotificationState,
    reduceProjectsTransferNotificationState,
    reduceZoneCreationNotificationState,
    reduceProjectCreationNotificationState,
    reduceZoneEditionNotificationState,
    reduceAuthenticationNotificationState,
    reduceTaskEditionNotificationState,
    reduceAccountCreationNotificationState,
    reduceAccountCreationConfirmationNotificationState,
    reduceTaskStatusCreationNotificationState,
    reduceUserPreferencesEditionNotificationState,
    reduceListCreationNotificationState,
    reduceRemoveTaskState,
    reduceAddPlanzoneMaterialResourceNotificationState,
    reduceTaskStatusEditionNotificationState,
    reduceTaskCreationNotificationState,
    reduceListEditionNotificationState,
    reduceRemoveProjectState,
    reduceRemoveListState,
    reduceAddPlanzoneVirtualResourceNotificationState,
    reduceAddProjectMaterialResourceNotificationState,
    reduceAddProjectVirtualResourceNotificationState,
    reduceAllocateResourcesNotificationState,
    reduceProjectWeatherReportEditionNotificationState,
    reduceRemoveResourceFromProjectNotificationState,
    reduceEditAllocationNotificationState,
    reduceMilestoneEditionNotificationState,
    reduceRemoveAllocationNotificationState,
    reduceRemoveTaskStatusState,
    reduceAddPlanzoneResourceToProjectNotificationState,
    reduceAddProjectExternalLinkNotificationState,
    reduceAssignTagNotificationState,
    reduceChangePlanzoneNotificationState,
    reduceCreateMilestoneNotificationState,
    reduceEditPersonalWeeklyCalendarNotificationState,
    reduceEditProjectNotificationState,
    reduceEditProjectExternalLinkNotificationState,
    reduceLogOutNotificationState,
    reduceMarkMilestoneAsCompletedNotificationState,
    reduceMoveKanbanTaskNotificationState,
    reduceMoveListElementNotificationState,
    reduceRemoveProjectExternalLinkNotificationState,
    reduceRemoveProjectStatusNotificationState,
    reduceCreateProjectStatusNotificationState,
    reduceRemoveTagNotificationState,
    reduceEditProjectReportTemplateNotificationState,
    reduceSubmitProjectReportNotificationState,
    reduceExportDataNotificationState,
    reduceExportBiRunNotificationState,
    reduceConfigureProjectToolsNotificationState,
    reduceMarkTaskAsCompletedNotificationState,
    reduceEditResourceNotificationState,
    reduceRemoveResourceFromPlanzoneNotificationState,
    reduceUpdatePlanzoneContactsNotificationState,
    reduceResourceGroupRemoveNotificationState,
    reduceResourceGroupEditionNotificationState,
    reduceAddResourceToResourceGroupNotificationState,
    reduceAddResourceGroupNotificationState,
    reduceRemoveResourceFromResourceGroupNotificationState,
    reduceEditProjectResourceNotificationState,
    reduceEventCreationNotificationState,
    reduceRemoveMilestoneState,
    reduceChangePasswordNotificationState,
    reduceAddPlanzoneResourcesToProjectNotificationState,
    reduceCreateProjectModelFromProjectNotificationState,
    reduceInviteMembersNotificationState,
    reduceFillAccountNotificationState,
    reduceCreateProjectPortfolioNotificationState,
    reduceEditProjectPortfolioNotificationState,
    reduceSendActivityReportNotificationState,
    reduceSaveTimeRecordNotificationState,
    reduceValidateTimeRecordsUpToDateNotificationState,
    reduceEventEditionNotificationState,
    reduceEventRetrievalNotificationState,
    reduceRemoveEventNotificationState,
    reduceMilestoneRetrievalNotificationState,
    reduceTaskRetrievalNotificationState,
    reduceListRetrievalNotificationState,
    reduceUnavailabilityCreationNotificationState,
    reduceEditUnavailabilityNotificationState,
    reduceUploadDocumentNotificationState,
    reduceDownloadDocumentContentNotificationState,
    reduceDownloadFolderContentNotificationState,
    reduceCreateCalendarHolidaysNotificationState,
    reduceRemoveCalendarHolidaysNotificationState,
    reduceRemoveHolidayNotificationState,
    reduceUseProjectModelNotificationState,
    reduceStartDiscussionNotificationState,
    reduceDuplicateProjectNotificationState,
    reduceAdjustWorkloadNotificationState,
    reduceAdjustCapacityNotificationState,
    reduceRemoveOrganizationalUnitState,
    reduceMoveOrganizationalUnitState,
    reduceUpdateOrganizationalUnitState,
  ].reduce(
    (nextState, reduceState) =>
      removeSuccessNotification(reduceState(nextState, event)),
    state,
  );
}

export type { State, Selector };

export { reduceState, initialState, createSelector };
